import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import pinkBox from '../../../../assets/challenge/pinkGift.png';
import purpleBox from '../../../../assets/challenge/purpleGift.png';
import crownPillowImg from '../../../../assets/crown-dialog/crown-pillow.png';
import type { CoinType } from '../../../../stores/cointype/CoinTypeStore';
import { type MissionTypeEnum } from '../../../../stores/daily-missions/types';
import { formatValueWithK } from '../../../../utils/helpers';
import { GAME_CARD_ID_PREFIX } from '../../../GamesList/constants';
import { getProgressBarTitle } from '../../utils';

import classes from './ProgressBar.module.scss';
import { useProgressBar } from './useProgressBar';

type ProgressBarProps = {
  max: number;
  current: number;
  className?: string;
  coinType: CoinType;
  missionId?: string;
  borderStyle?: string;
  gameImageUrl?: string;
  missionType: MissionTypeEnum;
  giftTooltip?: React.ReactNode;
  staticMessage?: React.ReactNode;
  disabled?: boolean;
  disableAnimation?: boolean;
  gameId?: number;
  integerProgress?: boolean;
  onClick?: () => void;
  onAnimationStart?: () => void;
  onAnimationEnd?: () => void;
  onGameClick?: () => void;
  onGiftClick?: () => void;
};

export const ProgressBar = ({
  max,
  current,
  className,
  onClick,
  coinType,
  missionId,
  borderStyle,
  onAnimationStart,
  onAnimationEnd,
  gameImageUrl,
  giftTooltip,
  missionType,
  staticMessage,
  disableAnimation,
  disabled,
  gameId,
  integerProgress,
  onGameClick,
  onGiftClick,
}: ProgressBarProps) => {
  const { t } = useTranslation();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const barText = getProgressBarTitle(missionType, coinType, t);

  useEffect(() => {
    let timeout: NodeJS.Timeout | number = 0;
    if (isTooltipVisible) {
      timeout = setTimeout(() => {
        setIsTooltipVisible(false);
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [isTooltipVisible]);

  const giftClickHandler = () => {
    if (!giftTooltip) return;
    onGiftClick?.();
    setIsTooltipVisible(!isTooltipVisible);
  };

  const animationEndTimeout = useRef<NodeJS.Timeout | number>(0);

  const onGameCircleClick = () => {
    onGameClick?.();
    if (gameId && animationEndTimeout.current === 0) {
      const gameCard = document.querySelector(`#${GAME_CARD_ID_PREFIX}${gameId}`);
      if (gameCard) {
        gameCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        gameCard.classList.add('game-card-highlight');
        clearTimeout(animationEndTimeout.current);
        animationEndTimeout.current = setTimeout(() => {
          gameCard.classList.remove('game-card-highlight');
          clearTimeout(animationEndTimeout.current);
          animationEndTimeout.current = 0;
        }, 4000);
      }
    }
  };

  const { progress, progressDisplay } = useProgressBar({
    current,
    max,
    disableAnimation,
    integerProgress,
    onAnimationEnd,
    onAnimationStart,
    missionId,
  });

  return (
    <div className={classNames(classes.dailyMissionBar, className)}>
      <div className={classes.wrapper}>
        <div className={classes.progressParent} style={borderStyle ? { border: borderStyle } : {}} onClick={onClick}>
          <>
            <div
              className={classNames(
                classes.dailyMissionBarProgress,
                coinType === 'GC' ? classes.gcColor : classes.scColor,
              )}
              style={{ width: `${(progress / max) * 100}%` }}
            />
            <div className={classes.text}>
              {staticMessage ? (
                staticMessage
              ) : (
                <>
                  {progressDisplay}/{formatValueWithK(max, t)}
                  <span data-testid="dailyMission-progress-bar" style={{ marginLeft: '4px' }}>
                    {barText}
                  </span>
                </>
              )}
            </div>
          </>
        </div>
        {!disabled && (
          <div className={classes.giftBox} onClick={giftClickHandler}>
            <>
              {coinType === 'SC' ? <img alt="GiftBox" src={pinkBox} /> : <img alt="GiftBox" src={purpleBox} />}
              {isTooltipVisible && <div className={classes.giftboxTooltip}>{giftTooltip}</div>}
            </>
          </div>
        )}
        {!disabled && (
          <div className={classes.gameImage}>
            <div className={classes.circle}></div>
            {gameImageUrl ? (
              <div className={classes.dynamic} onClick={onGameCircleClick}>
                <img alt="Game" src={gameImageUrl} />
              </div>
            ) : (
              <img alt="crown" loading="lazy" src={crownPillowImg} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
